import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Download, X } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { numberToWords } from "../../lib/currency-utils";
import { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, AlignmentType, WidthType, BorderStyle } from "docx";
import companyLogo from "../../assets/logo.svg";
import apiService from "../../services/apiService";

interface POPreviewModalProps {
  po: any;
  customer:any;
  isOpen: boolean;
  currentStatus:string;
  onClose: () => void;
}

export const POPreviewModal: React.FC<POPreviewModalProps> = ({ po,currentStatus, isOpen, onClose,customer }) => {
  const { toast } = useToast();
  const [signUrl, setSignUrl] = useState<string>('');

  if (!po) return null;

  const printRef = useRef<HTMLDivElement>(null);

  const handleDownload = async (format: "pdf" | "docx" = "pdf") => {
    if (format === "pdf") {
      if (!printRef.current) return;
      try {
     
        // Capture with high quality settings
        const dataUrl = await toPng(printRef.current, { 
          backgroundColor: "#ffffff", 
          pixelRatio: 2, // Higher ratio for sharper output
          quality: 1 // High quality with minimal compression
        });
        
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const img = new Image();
        img.src = dataUrl;
        await new Promise((res) => (img.onload = res));
        const imgProps = { width: img.width, height: img.height };

        // Calculate how many pages we need
        const imgWidthMm = pageWidth;
        const imgHeightMm = (imgProps.height * pageWidth) / imgProps.width;
        // const pagesNeeded = Math.ceil(imgHeightMm / pageHeight);
        const pagesNeeded = Math.floor((imgHeightMm - 1) / pageHeight) + 1;

        // Add pages using JPEG with medium compression for balanced quality/size
        for (let page = 0; page < pagesNeeded; page++) {
          if (page > 0) {
            pdf.addPage();
          }

          const yOffset = -(page * pageHeight);
          pdf.addImage(dataUrl, "JPEG", 0, yOffset, imgWidthMm, imgHeightMm, undefined, "MEDIUM");
        }

        pdf.save(`PO-${po.purchaseOrderCode}.pdf`);

        toast({
          title: "Downloaded",
          description: `PO ${po.id} saved as PDF (${pagesNeeded} page${pagesNeeded > 1 ? "s" : ""})`,
        });
      } catch (e) {
        toast({ title: "Download failed", description: "Please try again.", variant: "destructive" as any });
      }
    } else {
      // DOCX Export - Complete rewrite to match brand design
      try {
        const supplier = po.supplier || {};
        // const customer = po.customer || {};
        
        // Create items table rows for DOCX
        const itemTableRows: TableRow[] = [];
        
        // Determine GST treatment
        const hasIgst = po.lines.some((line: any) => (line.igst || 0) > 0);
        const hasUtgst = po.lines.some((line: any) => (line.utgst || 0) > 0);
        
        // Header row
        const headerCells = [
          new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "#", bold: true, size: 18 })] })], width: { size: 5, type: WidthType.PERCENTAGE } }),
          new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Item & Description", bold: true, size: 18 })] })], width: { size: 30, type: WidthType.PERCENTAGE } }),
          new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Qty", bold: true, size: 18 })] })], width: { size: 8, type: WidthType.PERCENTAGE } }),
          new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Base Rate", bold: true, size: 18 })] })], width: { size: 12, type: WidthType.PERCENTAGE } }),
        ];
        
        if (hasIgst) {
          headerCells.push(
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "IGST", bold: true, size: 18 })], })], columnSpan: 2 })
          );
        } else if (hasUtgst) {
          headerCells.push(
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "CGST", bold: true, size: 18 })], })], columnSpan: 2 }),
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "UTGST", bold: true, size: 18 })], })], columnSpan: 2 })
          );
        } else {
          headerCells.push(
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "CGST", bold: true, size: 18 })], })], columnSpan: 2 }),
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "SGST", bold: true, size: 18 })], })], columnSpan: 2 })
          );
        }
        
        headerCells.push(
          new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Total Amount", bold: true, size: 18 })] })], width: { size: 15, type: WidthType.PERCENTAGE } })
        );
        
        // Subheader row for % and Amt
        const subHeaderCells = [
          new TableCell({ children: [new Paragraph({ text: "" })], columnSpan: 4 }),
        ];
        
        if (hasIgst) {
          subHeaderCells.push(
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "%", bold: true, size: 16 })] })] }),
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Amt", bold: true, size: 16 })] })] })
          );
        } else if (hasUtgst) {
          subHeaderCells.push(
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "%", bold: true, size: 16 })] })] }),
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Amt", bold: true, size: 16 })] })] }),
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "%", bold: true, size: 16 })] })] }),
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Amt", bold: true, size: 16 })] })] })
          );
        } else {
          subHeaderCells.push(
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "%", bold: true, size: 16 })] })] }),
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Amt", bold: true, size: 16 })] })] }),
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "%", bold: true, size: 16 })] })] }),
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Amt", bold: true, size: 16 })] })] })
          );
        }
        
        subHeaderCells.push(new TableCell({ children: [new Paragraph({ text: "" })] }));
        
        itemTableRows.push(new TableRow({ children: headerCells }));
        itemTableRows.push(new TableRow({ children: subHeaderCells }));
        
        // Data rows
        let subtotal = 0;
        let totalCgst = 0;
        let totalSgst = 0;
        let totalIgst = 0;
        let totalUtgst = 0;
        
        po.lines.forEach((asset: any, idx: number) => {
          const itemSubtotal = asset.unitPrice * asset.qty;
          const cgst = (itemSubtotal * (asset.cgst || 0)) / 100;
          const sgst = (itemSubtotal * (asset.sgst || 0)) / 100;
          const igst = (itemSubtotal * (asset.igst || 0)) / 100;
          const utgst = (itemSubtotal * (asset.utgst || 0)) / 100;
          const total = itemSubtotal + cgst + sgst + igst + utgst;
          
          subtotal += itemSubtotal;
          totalCgst += cgst;
          totalSgst += sgst;
          totalIgst += igst;
          totalUtgst += utgst;
          
          const dataCells = [
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: (idx + 1).toString(), size: 18 })] })] }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: asset.item || asset.description, size: 18 })] })] }),
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: asset.qty.toString(), size: 18 })] })] }),
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: asset.unitPrice.toLocaleString('en-IN'), size: 18 })] })] }),
          ];
          
          if (hasIgst) {
            dataCells.push(
              new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: `${asset.igst || 0}%`, size: 18 })] })] }),
              new TableCell({ children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: igst.toLocaleString('en-IN'), size: 18 })] })] })
            );
          } else if (hasUtgst) {
            dataCells.push(
              new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: `${asset.cgst || 0}%`, size: 18 })] })] }),
              new TableCell({ children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: cgst.toLocaleString('en-IN'), size: 18 })] })] }),
              new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: `${asset.utgst || 0}%`, size: 18 })] })] }),
              new TableCell({ children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: utgst.toLocaleString('en-IN'), size: 18 })] })] })
            );
          } else {
            dataCells.push(
              new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: `${asset.cgst || 0}%`, size: 18 })] })] }),
              new TableCell({ children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: cgst.toLocaleString('en-IN'), size: 18 })] })] }),
              new TableCell({ children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: `${asset.sgst || 0}%`, size: 18 })] })] }),
              new TableCell({ children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: sgst.toLocaleString('en-IN'), size: 18 })] })] })
            );
          }
          
          dataCells.push(
            new TableCell({ children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: total.toLocaleString('en-IN'), size: 18 })] })] })
          );
          
          itemTableRows.push(new TableRow({ children: dataCells }));
        });
        
        const grandTotal = subtotal + totalCgst + totalSgst + totalIgst + totalUtgst;
        
        const doc = new Document({
          sections: [
            {
              properties: {},
              children: [
                // Header with logo and company name
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: BorderStyle.SINGLE, size: 1 },
                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                    left: { style: BorderStyle.SINGLE, size: 1 },
                    right: { style: BorderStyle.SINGLE, size: 1 },
                    insideHorizontal: { style: BorderStyle.NONE },
                    insideVertical: { style: BorderStyle.NONE },
                  },
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          width: { size: 60, type: WidthType.PERCENTAGE },
                          children: [
                            new Paragraph({ 
                              children: [new TextRun({ text: "ASTRAFIN NEOSOLUTIONS", size: 32, bold: true })] 
                            }),
                            new Paragraph({ 
                              children: [new TextRun({ text: "PRIVATE LIMITED", size: 28, bold: true })] 
                            }),
                            new Paragraph({ 
                              children: [new TextRun({ text: "14, PH2, Bluebell, Klassik Landmark, Off. Sarjapur Road", size: 18 })] 
                            }),
                            new Paragraph({ 
                              children: [new TextRun({ text: "Bengaluru Urban Karnataka 560035", size: 18 })] 
                            }),
                            new Paragraph({ 
                              children: [new TextRun({ text: "India", size: 18 })] 
                            }),
                            new Paragraph({ 
                              children: [new TextRun({ text: "GSTIN 29ABBCA4070B1Z3", size: 18 })] 
                            }),
                            new Paragraph({ 
                              children: [new TextRun({ text: "9869217190 Email: platform@astrafin.org,", size: 18 })] 
                            }),
                            new Paragraph({ 
                              children: [new TextRun({ text: "astrafin.neo@gmail.com", size: 18 })] 
                            }),
                          ],
                        }),
                        new TableCell({
                          width: { size: 40, type: WidthType.PERCENTAGE },
                          children: [
                            new Paragraph({ 
                              alignment: AlignmentType.CENTER,
                              children: [new TextRun({ text: "Purchase", size: 56, color: "808080" })] 
                            }),
                            new Paragraph({ 
                              alignment: AlignmentType.CENTER,
                              children: [new TextRun({ text: "order", size: 56, color: "808080" })] 
                            }),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
                
                new Paragraph({ text: "" }), // Spacing
                
                // Vendor information section
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: BorderStyle.SINGLE, size: 1 },
                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                    left: { style: BorderStyle.SINGLE, size: 1 },
                    right: { style: BorderStyle.SINGLE, size: 1 },
                    insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
                    insideVertical: { style: BorderStyle.SINGLE, size: 1 },
                  },
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          width: { size: 50, type: WidthType.PERCENTAGE },
                          children: [
                            new Paragraph({ children: [new TextRun({ text: "Vendor Name:", bold: true, size: 20 })] }),
                            new Paragraph({ children: [new TextRun({ text: supplier?.company_name || supplier.name || "N/A", size: 20, bold: true })] }),
                            new Paragraph({ children: [new TextRun({ text: `Company ID: ${supplier.id || "N/A"}`, size: 18 })] }),
                            new Paragraph({ 
                              children: [new TextRun({ 
                                text: supplier.addresses?.[0] 
                                  ? `${supplier.addresses[0].address_line1}, ${supplier.addresses[0].city}, ${supplier.addresses[0].state} - ${supplier.addresses[0].pincode}` 
                                  : supplier.address || "Address not provided",
                                size: 18
                              })] 
                            }),
                            new Paragraph({ children: [new TextRun({ text: `Vendor GST: ${supplier.gstin || supplier.addresses?.[0]?.gstin || "N/A"}`, size: 18 })] }),
                          ],
                        }),
                        new TableCell({
                          width: { size: 50, type: WidthType.PERCENTAGE },
                          children: [
                            new Paragraph({ children: [new TextRun({ text: `Ctc person : ${supplier.primary_contact_name || supplier.name || "N/A"}`, size: 18 })] }),
                            new Paragraph({ children: [new TextRun({ text: `Email : ${supplier.primary_contact_email || supplier.email || "N/A"}`, size: 18, color: "0000FF", underline: {} })] }),
                            new Paragraph({ children: [new TextRun({ text: `Mobile NO:`, size: 18 })] }),
                          ],
                        }),
                      ],
                    }),
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({ children: [new TextRun({ text: "PURCHASE ORDER", size: 20, bold: true })] }),
                          ],
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({ children: [new TextRun({ text: `PO No.${po.id}`, size: 18 })] }),
                            new Paragraph({ children: [new TextRun({ text: `Date: ${new Date(po.createdAt).toLocaleDateString('en-GB').replace(/\//g, '-')}`, size: 18 })] }),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
                
                new Paragraph({ text: "" }), // Spacing
                
                // Bill To and Ship To section
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: BorderStyle.SINGLE, size: 1 },
                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                    left: { style: BorderStyle.SINGLE, size: 1 },
                    right: { style: BorderStyle.SINGLE, size: 1 },
                    insideHorizontal: { style: BorderStyle.NONE },
                    insideVertical: { style: BorderStyle.SINGLE, size: 1 },
                  },
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          width: { size: 50, type: WidthType.PERCENTAGE },
                          children: [
                            new Paragraph({ children: [new TextRun({ text: "Bill To", bold: true, size: 20 })] }),
                            new Paragraph({ children: [new TextRun({ text: customer.name || "ABC Private Limited", size: 18, bold: true })] }),
                            new Paragraph({ children: [new TextRun({ text: "M/s. ABC Private Ltd.", size: 18 })] }),
                            new Paragraph({ children: [new TextRun({ text: "TR-100, ,", size: 18 })] }),
                            new Paragraph({ children: [new TextRun({ text: "TTC Industrial Estate, Mahape,", size: 18 })] }),
                            new Paragraph({ children: [new TextRun({ text: "Navi Mumbai – 400709.", size: 18 })] }),
                            new Paragraph({ children: [new TextRun({ text: `GSTIN ${customer.gstin || "27ABCDE1234F5A1"}`, size: 18 })] }),
                          ],
                        }),
                        new TableCell({
                          width: { size: 50, type: WidthType.PERCENTAGE },
                          children: [
                            new Paragraph({ children: [new TextRun({ text: "Ship To", bold: true, size: 20 })] }),
                            ...(po?.customerGST?.trim()
                              ? [
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: `GSTIN ${po.customerGST.trim()}`,
                                        size: 18,
                                      }),
                                    ],
                                  }),
                                ]
                              : []),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
                
                new Paragraph({ text: "" }), // Spacing
                
                // Items table
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: BorderStyle.SINGLE, size: 1 },
                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                    left: { style: BorderStyle.SINGLE, size: 1 },
                    right: { style: BorderStyle.SINGLE, size: 1 },
                    insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
                    insideVertical: { style: BorderStyle.SINGLE, size: 1 },
                  },
                  rows: itemTableRows,
                }),
                
                new Paragraph({ text: "" }), // Spacing
                
                // Totals section
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: BorderStyle.SINGLE, size: 1 },
                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                    left: { style: BorderStyle.SINGLE, size: 1 },
                    right: { style: BorderStyle.SINGLE, size: 1 },
                    insideHorizontal: { style: BorderStyle.NONE },
                    insideVertical: { style: BorderStyle.SINGLE, size: 1 },
                  },
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          width: { size: 50, type: WidthType.PERCENTAGE },
                          children: [
                            new Paragraph({ children: [new TextRun({ text: "Total Amount In Words", bold: true, size: 20 })] }),
                            new Paragraph({ children: [new TextRun({ text: `${numberToWords(Math.round(grandTotal))} Only`, size: 18 })] }),
                          ],
                        }),
                        new TableCell({
                          width: { size: 50, type: WidthType.PERCENTAGE },
                          children: [
                            new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: `Sub Total ${subtotal.toLocaleString('en-IN')}`, size: 18 })] }),
                            ...(totalIgst > 0 
                              ? [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: `IGST (18%) ${totalIgst.toLocaleString('en-IN')}`, size: 18 })] })]
                              : [
                                  ...(totalCgst > 0 ? [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: `CGST ${totalCgst.toLocaleString('en-IN')}`, size: 18 })] })] : []),
                                  ...(totalSgst > 0 ? [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: `SGST ${totalSgst.toLocaleString('en-IN')}`, size: 18 })] })] : []),
                                  ...(totalUtgst > 0 ? [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: `UTGST ${totalUtgst.toLocaleString('en-IN')}`, size: 18 })] })] : []),
                                ]
                            ),
                            new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: `Total ${grandTotal.toLocaleString('en-IN')}`, size: 20, bold: true })] }),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
                
                new Paragraph({ text: "" }), // Spacing
                
                // GSTIN/PAN and Instructions section
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: BorderStyle.SINGLE, size: 1 },
                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                    left: { style: BorderStyle.SINGLE, size: 1 },
                    right: { style: BorderStyle.SINGLE, size: 1 },
                    insideHorizontal: { style: BorderStyle.NONE },
                    insideVertical: { style: BorderStyle.NONE },
                  },
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({ children: [new TextRun({ text: "Astrafin GSTIN: 29ABBCA4070B1Z3", size: 18, bold: true })] }),
                            new Paragraph({ children: [new TextRun({ text: "Astrafin PAN: ABBCA4070B", size: 18, bold: true })] }),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
                
                new Paragraph({ text: "" }), // Spacing
                
                // Instructions
                new Paragraph({ children: [new TextRun({ text: "Instructions:", bold: true, size: 20 })] }),
                new Paragraph({ 
                  numbering: { reference: "default-numbering", level: 0 },
                  children: [new TextRun({ text: "Material shall not be accepted without a Delivery Challan, an Inspection report and a Raw Material Test report.", size: 18 })] 
                }),
                new Paragraph({ 
                  numbering: { reference: "default-numbering", level: 0 },
                  children: [new TextRun({ text: "P.O. No. & Item Code of each item must be specified on the Delivery Challan & Invoice. Without which the goods shall not be accepted.", size: 18 })] 
                }),
                new Paragraph({ 
                  numbering: { reference: "default-numbering", level: 0 },
                  children: [new TextRun({ text: "Material will not be accepted if beyond the specified quantity tolerance limit.", size: 18 })] 
                }),
                
                new Paragraph({ text: "" }), // Spacing
                new Paragraph({ text: "" }), // Spacing
                
                // Signature section
                new Paragraph({ 
                  alignment: AlignmentType.RIGHT,
                  children: [new TextRun({ text: "For Astrafin Neosolutions Private Limited", size: 20, bold: true })] 
                }),
                new Paragraph({ text: "" }), // Spacing
                new Paragraph({ text: "" }), // Spacing
                new Paragraph({ text: "" }), // Spacing
                new Paragraph({ 
                  alignment: AlignmentType.RIGHT,
                  children: [new TextRun({ text: "Authorised Signatory", size: 18, bold: true })] 
                }),
              ],
            },
          ],
          numbering: {
            config: [
              {
                reference: "default-numbering",
                levels: [
                  {
                    level: 0,
                    format: "decimal",
                    text: "%1.",
                    alignment: AlignmentType.LEFT,
                  },
                ],
              },
            ],
          },
        });

        const blob = await Packer.toBlob(doc);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `PO-${po.purchaseOrderCode}.docx`;
        a.click();
        window.URL.revokeObjectURL(url);

        toast({ title: "Downloaded", description: `PO ${po.purchaseOrderCode} saved as DOCX` });
      } catch (e) {
        console.error("DOCX generation error:", e);
        toast({ title: "Download failed", description: "Please try again.", variant: "destructive" as any });
      }
    }
  };

  // Helper function to format currency in Indian style
  const formatIndianCurrency = (amount: number) => {
    return amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const calcItem = (asset: any) => {
    const subtotal = asset.unitPrice * asset.qty;
    const cgstP = asset.cgst ?? 0;
    const sgstP = asset.sgst ?? 0;
    const igstP = asset.igst ?? 0;
    const utgstP = asset.utgst ?? 0;
    const cgstAmt = (subtotal * cgstP) / 100;
    const sgstAmt = (subtotal * sgstP) / 100;
    const igstAmt = (subtotal * igstP) / 100;
    const utgstAmt = (subtotal * utgstP) / 100;
    const total = subtotal + cgstAmt + sgstAmt + igstAmt + utgstAmt;
    return { subtotal, cgstP, sgstP, igstP, utgstP, cgstAmt, sgstAmt, igstAmt, utgstAmt, total };
  };

  const totals = po.lines.reduce(
    (acc: any, line: any) => {
      const t = calcItem(line);
  
      // Subtotal
      acc.subtotal += t.subtotal;
      acc.grand += t.total;
  
      // Group CGST by percent
      if (t.cgstP > 0) {
        acc.cgst[t.cgstP] = (acc.cgst[t.cgstP] || 0) + t.cgstAmt;
      }
  
      // Group SGST
      if (t.sgstP > 0) {
        acc.sgst[t.sgstP] = (acc.sgst[t.sgstP] || 0) + t.sgstAmt;
      }
  
      // Group IGST
      if (t.igstP > 0) {
        acc.igst[t.igstP] = (acc.igst[t.igstP] || 0) + t.igstAmt;
      }
  
      // Group UTGST
      if (t.utgstP > 0) {
        acc.utgst[t.utgstP] = (acc.utgst[t.utgstP] || 0) + t.utgstAmt;
      }
  
      return acc;
    },
    {
      subtotal: 0,
      cgst: {},
      sgst: {},
      igst: {},
      utgst: {},
      grand: 0
    }
  );

  const supplier = po.supplier || {};
  const customerGst = po?.customerGST?.trim() || "";
  // const customer = po.customer || {};

  // Determine GST treatment
  const hasIgst = po.lines.some((line: any) => (line.igst || 0) > 0);
  const hasUtgst = po.lines.some((line: any) => (line.utgst || 0) > 0);

  useEffect(()=>{
    if(currentStatus == 'approved'){
      getdata();
    }
  },[currentStatus])

  const getdata = async () => {
    const response = await apiService.get<any>(`/purchase-order/get-sign/${po.customerId}`);
    setSignUrl(response.data);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>

      <DialogContent className="m-0 h-full w-full p-0 md:max-w-full">
        
         {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between shadow-sm z-10">
          <h2 className="text-xl font-semibold">Purchase Order Preview - {po.purchaseOrderCode}</h2>
          <div className="flex items-center space-x-3">
            <Button onClick={() => handleDownload("pdf")} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            {/* <Button onClick={() => handleDownload("docx")} variant="outline" size="sm">
              <FileText className="h-4 w-4 mr-2" />
              DOCX
            </Button> */}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6 bg-muted/30">
          <div className="flex justify-center">
            {/* A4 Canvas */}
            <div ref={printRef} className="bg-white shadow-2xl w-[794px] min-h-[1123px] p-8" style={{ fontFamily: 'Arial, sans-serif' }}>
              
              {/* Company Header with Logo */}
              <table style={{ width: '100%', border: '1px solid #9E9E9E', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ borderLeft: '1px solid #9E9E9E', borderTop: '1px solid #9E9E9E', borderBottom: '1px solid #9E9E9E', padding: '10px', width: '90%', verticalAlign: 'top' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' ,marginLeft:'2%'}}>
                        <img src={companyLogo} alt="Astrafin Logo" style={{ width: '130px', height: 'auto' }} />
                        <div>
                          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>ASTRAFIN NEOSOLUTIONS PRIVATE LIMITED</div>
                          {/* <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}></div> */}
                          <div style={{ fontSize: '11px', lineHeight: '1.4' ,textAlign:'center'}}>
                            <div> 14th Floor, PH2, Bluebell, Klassik Landmark, Off. Sarjapur Road</div>
                            <div>Bengaluru Urban Karnataka 560035</div>
                            {/* <div>India</div> */}
                            <div style={{ marginTop: '4px' }}>GSTIN 29ABBCA4070B1Z3 | PAN ABBCA4070B</div>
                            <div>+91-7276308170 📧Email: platform@astrafin.org,</div>
                            <div>mukul@astrafin.org</div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ borderRight: '1px solid #9E9E9E', borderTop: '1px solid #9E9E9E', borderBottom: '1px solid #9E9E9E',padding: '10px', width: '10%',  verticalAlign: 'middle' }}>
                      <div style={{textAlign:'right',marginTop:'10%'}}>
 <div style={{ fontSize: '23px', fontWeight: 'normal', color: 'black', lineHeight: '1' }}>Purchase</div>
                      <div style={{ fontSize: '23px', fontWeight: 'normal', color: 'black' }}>order</div>
                      </div>
                     

                     
                    </td> 
                  </tr>
                </tbody>
              </table>

              {/* Vendor Information Section */}
              <table style={{ width: '100%',  borderRight: '1px solid #9E9E9E', borderLeft: '1px solid #9E9E9E', borderBottom: '1px solid #9E9E9E', borderCollapse: 'collapse', marginTop: '0' }}>
                <tbody>
                  <tr>
                    <td style={{ borderRight: '1px solid #9E9E9E', padding: '8px', width: '50%', verticalAlign: 'top' }}>
                      <div style={{ fontSize: '11px' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Vendor Name:</div>
                        <div style={{ fontWeight: 'bold', fontSize: '10px', marginBottom: '2px' }}>
                          {supplier?.company_name || supplier.name || "N/A"}
                        </div>
                        {/* <div style={{ marginBottom: '2px' }}>Company Name: {supplier.id || "N/A"}</div> */}
                        <div style={{ marginBottom: '2px' }}>
                          {po.supplierAddress
                            ? `${po.supplierAddress}`
                            : supplier.address || "Address not provided"}
                        </div>
                        <div>Vendor GST: {supplier.gstin || supplier.addresses?.[0]?.gstin || "N/A"} | PAN {supplier.pan || "N/A"}</div>
                      </div>
                    </td>
                    <td style={{  padding: '8px', width: '50%', verticalAlign: 'top' }}>
                      <div style={{ fontSize: '11px' }}>
                        <div className="flex justify-between" >
 <div style={{ fontSize: '11px', lineHeight: '1.4' }}>PO No : {po.purchaseOrderCode}</div>
 <div style={{ fontSize: '11px', lineHeight: '1.4' }}>
  Date:{' '}
  {new Date(po.approvedAt || po.createdAt)
    .toLocaleDateString('en-GB')
    .replace(/\//g, '-')}
</div>
</div>
<div style={{marginTop: '5%' }}>
                        <div style={{ marginBottom: '2px' }}>
                          Contact Person : <span style={{ fontWeight: 'bold' }}>{supplier.spocDetails?.find((spoc: any) => spoc.id === po.supplierSpocId)?.name || "N/A"}</span>
                        </div>
                        <div style={{ marginBottom: '2px' }}>
                          Email : <span style={{ color: '#0000FF', textDecoration: 'underline' }}>
                            {supplier.spocDetails?.find((spoc: any) => spoc.id === po.supplierSpocId)?.email || supplier.email || "N/A"}
                          </span>
                        </div>
                        <div>Mobile NO: {supplier.spocDetails?.find((spoc: any) => spoc.id === po.supplierSpocId)?.mobile || "N/A"}</div>
                      </div>
                      </div>
                    </td>
                  </tr>
                  {/* <tr>
                    <td style={{ border: '1px solid black', padding: '8px', fontWeight: 'bold', fontSize: '12px' }}>
                      PURCHASE ORDER
                    </td>
                    <td style={{ border: '1px solid black', padding: '8px', fontSize: '11px' }}>
                      <div>PO No.{po.id}</div>
                      <div>Date: {new Date(po.createdAt).toLocaleDateString('en-GB').replace(/\//g, '-')}</div>
                    </td>
                  </tr> */}
                </tbody>
              </table>

              {/* Bill To and Ship To Section */}
                <table style={{ width: '100%', borderLeft: '1px solid #9E9E9E', borderCollapse: 'collapse', marginTop: '0' }}>
                <tbody>
                  <tr>
                    <td style={{ borderRight: '1px solid #9E9E9E',  width: '50%', verticalAlign: 'top' }}>
                      <div style={{ fontSize: '11px' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px',borderBottom: '1px solid #9E9E9E',backgroundColor: '#f5f5f5' , padding: '4px', }}>Bill To</div>
                        <div style={{ fontWeight: 'bold', marginBottom: '2px',padding: '4px' }}>ASTRAFIN NEOSOLUTIONS PRIVATE LIMITED</div>
                        <div style={{paddingLeft: '4px' }} >14th Floor, PH2, Bluebell, Klassik Landmark, Off. Sarjapur Road</div>
                            <div style={{paddingLeft: '4px' }} >Bengaluru Urban Karnataka 560035</div>
                            {/* <div style={{paddingLeft: '4px' }} >India</div> */}
                            <div style={{ paddingLeft: '4px' }}>GSTIN 29ABBCA4070B1Z3 | PAN ABBCA4070B</div>
                        {/* <div style={{padding: '4px' }}>GSTIN {customer.gstin || "27ABCDE1234F5A1"}</div> */}
                      </div>
                    </td>
                    <td style={{ borderRight: '1px solid #9E9E9E', width: '50%', verticalAlign: 'top' }}>
                      <div style={{ fontSize: '11px' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' ,borderBottom: '1px solid #9E9E9E',backgroundColor: '#f5f5f5' , padding: '4px',}}>Ship To</div>
                        <div style={{ fontWeight: 'bold', marginBottom: '2px',padding: '4px' }}>{customer?.entityName || "N/A"}</div>
                        <div style={{paddingLeft: '4px' }} >{po.deliveryAddress}</div>
                        {customerGst && (
                          <div style={{ padding: '4px' }}>GSTIN {customerGst}</div>
                        )}

                            {/* <div style={{paddingLeft: '4px' }} >Bengaluru Urban Karnataka 560035</div>
                            <div style={{paddingLeft: '4px' }} >India</div>
                            <div style={{ paddingLeft: '4px' }}>GSTIN 29ABBCA4070B1Z3 | PAN ABBCA4070B</div> */}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Items Table */}
              <table style={{ width: '100%',  borderRight: '1px solid #9E9E9E', borderLeft: '1px solid #9E9E9E', borderBottom: '1px solid #9E9E9E', borderCollapse: 'collapse', marginTop: '0', fontSize: '10px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th rowSpan={2} style={{ border: '1px solid #9E9E9E', paddingLeft: '6px', textAlign: 'center', width: '4%' }}>#</th>
                    <th rowSpan={2} style={{ border: '1px solid #9E9E9E', paddingLeft: '6px', textAlign: 'left', width: '35%' }}>Item & Description</th>
                    <th  rowSpan={2} style={{ border: '1px solid #9E9E9E', paddingLeft: '6px', textAlign: 'center', width: '6%' }}>Qty</th>
                    <th rowSpan={2} style={{ border: '1px solid #9E9E9E', paddingLeft: '6px', textAlign: 'center', width: '12%' }}>Base Rate</th>
                    {hasIgst ? (
                      <>
                        <th style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }} colSpan={2}>IGST</th>
                      </>
                    ) : hasUtgst ? (
                      <>
                        <th style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }} colSpan={2}>CGST</th>
                        <th style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }} colSpan={2}>UTGST</th>
                      </>
                    ) : (
                      <>
                        <th style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }} colSpan={2}>CGST</th>
                        <th style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }} colSpan={2}>SGST</th>
                      </>
                    )}
                    <th rowSpan={2} style={{ border: '1px solid #9E9E9E', paddingLeft: '6px', textAlign: 'center', width: '15%' }}>Total Amount</th>
                  </tr>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    {/* <th style={{ border: '1px solid black', padding: '4px' }} ></th> */}
                    {hasIgst ? (
                      <>
                        <th style={{ border: '1px solid #9E9E9E', padding: '4px', textAlign: 'center' }}>%</th>
                        <th style={{ border: '1px solid  #9E9E9E', padding: '4px', textAlign: 'center' }}>Amt</th>
                      </>
                    ) : hasUtgst ? (
                      <>
                        <th style={{ border: '1px solid #9E9E9E', padding: '4px', textAlign: 'center' }}>%</th>
                        <th style={{ border: '1px solid #9E9E9E', padding: '4px', textAlign: 'center' }}>Amt</th>
                        <th style={{ border: '1px solid #9E9E9E', padding: '4px', textAlign: 'center' }}>%</th>
                        <th style={{ border: '1px solid #9E9E9E', padding: '4px', textAlign: 'center' }}>Amt</th>
                      </>
                    ) : (
                      <>
                        <th style={{ border: '1px solid #9E9E9E', padding: '4px', textAlign: 'center' }}>%</th>
                        <th style={{ border: '1px solid #9E9E9E', padding: '4px', textAlign: 'center' }}>Amt</th>
                        <th style={{ border: '1px solid #9E9E9E', padding: '4px', textAlign: 'center' }}>%</th>
                        <th style={{ border: '1px solid #9E9E9E', padding: '4px', textAlign: 'center' }}>Amt</th>
                      </>
                    )}
                    {/* <th style={{ border: '1px solid black', padding: '4px' }}></th> */}
                  </tr>
                </thead>
                <tbody>
                  {po.lines.map((asset: any, idx: number) => {
                    const t = calcItem(asset);
                    return (
                      <tr key={asset.id}>
                        <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }}>{idx + 1}</td>
                        <td style={{ border: '1px solid #9E9E9E', padding: '6px' }}>
                          <div style={{ fontWeight: 'bold' }}>{asset.item || asset.description}</div>
                          {asset.item && asset.description && <div>{asset.description}</div>}
                        </td>
                        <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }}>{asset.qty}</td>
                        <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'right' }}>₹{formatIndianCurrency(asset.unitPrice)}</td>
                        {hasIgst ? (
                          <>
                            <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }}>{t.igstP}%</td>
                            <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'right' }}>₹{formatIndianCurrency(t.igstAmt)}</td>
                          </>
                        ) : hasUtgst ? (
                          <>
                            <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }}>{t.cgstP}%</td>
                            <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'right' }}>₹{formatIndianCurrency(t.cgstAmt)}</td>
                            <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }}>{t.utgstP}%</td>
                            <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'right' }}>₹{formatIndianCurrency(t.utgstAmt)}</td>
                          </>
                        ) : (
                          <>
                            <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }}>{t.cgstP}%</td>
                            <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'right' }}>₹{formatIndianCurrency(t.cgstAmt)}</td>
                            <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'center' }}>{t.sgstP}%</td>
                            <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'right' }}>₹{formatIndianCurrency(t.sgstAmt)}</td>
                          </>
                        )}
                        <td style={{ border: '1px solid #9E9E9E', padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>
                        ₹{formatIndianCurrency(t.total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Totals Section */}
              <table style={{ width: '100%', borderRight: '1px solid #9E9E9E', borderLeft: '1px solid #9E9E9E', borderBottom: '1px solid #9E9E9E', borderCollapse: 'collapse', marginTop: '0', fontSize: '10px' }}>
                <tbody>
                  <tr>
                    <td style={{ borderRight: '1px solid #9E9E9E', padding: '8px', width: '50%', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Total Amount In Words</div>
                      <div>Rupees {numberToWords(Math.round(totals.grand))} Only</div>
                    </td>
                    <td style={{  width: '22%'}}></td>
                    <td
  style={{
    padding: '8px',
    width: '28%',
    verticalAlign: 'top'
  }}
>
<div style={{ marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
  <span>Sub Total</span>
  <span>₹{formatIndianCurrency(totals.subtotal)}</span>
</div>

  {hasIgst ? (
   <>
   {Object.keys(totals.igst).map((p) => (
  <div key={'igst-' + p} style={{ marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
    <span>IGST {p}%</span>
    <span>₹{formatIndianCurrency(totals.igst[p])}</span>
  </div>
))}
   </>
  ) : (
    <>
{Object.keys(totals.cgst).map((p) => (
  <div key={'cgst-' + p} style={{ marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
    <span>CGST {p}%</span>
    <span>₹{formatIndianCurrency(totals.cgst[p])}</span>
  </div>
))}

{Object.keys(totals.sgst).map((p) => (
  <div key={'sgst-' + p} style={{ marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
    <span>SGST {p}%</span>
    <span>₹{formatIndianCurrency(totals.sgst[p])}</span>
  </div>
))}

{Object.keys(totals.utgst).map((p) => (
  <div key={'utgst-' + p} style={{ marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
    <span>UTGST {p}%</span>
    <span>₹{formatIndianCurrency(totals.utgst[p])}</span>
  </div>
))}
    </>
  )}

  <div
    style={{
      fontWeight: 'bold',
      fontSize: '12px',
      marginTop: '4px',
      display: 'flex',
      justifyContent: 'space-between'
    }}
  >
    <span>Total</span>
    <span>₹{formatIndianCurrency(totals.grand)}</span>
  </div>
</td>

                  </tr>
                </tbody>
              </table>

              {/* GSTIN and PAN Section */}
              {/* <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '0', fontSize: '10px' }}>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid black', padding: '8px' }}>
                      <div style={{ fontWeight: 'bold' }}>Astrafin GSTIN: 29ABBCA4070B1Z3</div>
                      <div style={{ fontWeight: 'bold' }}>Astrafin PAN: ABBCA4070B</div>
                    </td>
                  </tr>
                </tbody>
              </table> */}

              {/* Instructions Section */}
              <div style={{ marginTop: '12px', fontSize: '10px' }}>
  <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Instructions:</div>
  <ul
    style={{
      paddingLeft: '20px',
      margin: 0,
      lineHeight: '1.6',
      listStyleType: 'circle'
    }}
  >
    <li>Material shall not be accepted without a Delivery Challan, an Inspection report and a Raw Material Test report.</li>
    <li>P.O. No. & Item Code of each item must be specified on the Delivery Challan & Invoice. Without which the goods shall not be accepted.</li>
    <li>Material will not be accepted if beyond the specified quantity tolerance limit.</li>
  </ul>
</div>

              {/* Signature Section */}
              <div style={{ marginTop: '40px', textAlign: 'right', fontSize: '10px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>For Astrafin Neosolutions Private Limited</div>
                <div style={{ height: '60px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
  {currentStatus==='approved' && (
  <img
  src={signUrl ?
    (signUrl.startsWith('http') ?
    signUrl :
      `${import.meta.env.VITE_API_BASE_URL}/${signUrl}`) :
    ''}
  // src={signUrl}
  alt="Astrafin Logo"
  style={{ width: '130px', height: 'auto' }}
/>
  )}

</div>
                <div style={{ fontWeight: 'bold', borderTop: '1px solid black', display: 'inline-block', paddingTop: '4px' }}>
                  Authorised Signatory
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};